import { useEffect, useRef, useState } from "react"
import { Modal } from "components/ui/modals/Modal/Modal"
import { ConfirmModal } from "components/ui/modals/ConfirmModal/ConfirmModal"
import { ButtonPrimary } from "components/ui/buttons/ButtonPrimary/ButtonPrimary"
import { QuotationConfiguration, type TQuotationConfig } from "components/ui/QuotationConfiguration/QuotationConfiguration"
import {
	buildDeliveryDatesPayload,
	createEmptyDeliveryDate,
	hasClearedSavedDeliveryDates,
	hasIncompleteDeliveryDate,
} from "components/ui/QuotationConfiguration/quotationConfiguration.helper"
import { useGetQuotation, useUpdateQuotationConfiguration } from "hooks/api/quotation/quotations.hook"
import { useMessage } from "hooks/message.hook"
import { useApiErrorFormatter } from "hooks/apiErrorFormatter.hook"
import type { TUpdateQuotationConfigurationRequest } from "types/quotations.type"
import style from "./QuotationSettingsModal.module.scss"

type Props = {
	isModalOpen: boolean
	setModalClose: () => void
	quotationId?: number
}

type TPendingDateAction = { type: "remove" | "change"; index: number; date?: string }

const CLEARED_SAVED_DATES_MESSAGE =
	"At least one delivery date must remain. Change a date instead of removing the last one."

const DATE_CONFIRM_COPY = {
	remove: {
		title: "Remove date?",
		description:
			"Removing this date will clear it from all assigned erefs. You can change the date instead to update it everywhere.",
		confirmLabel: "Remove",
	},
	change: {
		title: "Change date?",
		description: "Changing this date will update it for all assigned erefs.",
		confirmLabel: "Change",
	},
}

export const QuotationSettingsModal = ({ isModalOpen, setModalClose, quotationId }: Props) => {
	const { data: quotation } = useGetQuotation(quotationId)
	const { mutate: updateConfig, isPending } = useUpdateQuotationConfiguration()
	const { success } = useMessage()
	const { formatError } = useApiErrorFormatter()

	const [config, setConfig] = useState<TQuotationConfig>(() => ({ deliveryDates: [createEmptyDeliveryDate()] }))
	const [pendingDateAction, setPendingDateAction] = useState<TPendingDateAction | null>(null)
	// Tracks which quotationId the form was seeded from (null = not seeded for the current open session).
	const seededIdRef = useRef<number | null>(null)

	// Seed the form from the saved configuration once per (open session, quotationId), so a post-save
	// refetch can't clobber in-progress edits, while still re-seeding if the target quotation changes.
	useEffect(() => {
		if (!isModalOpen) {
			seededIdRef.current = null
			setPendingDateAction(null)
			return
		}
		if (!quotation || seededIdRef.current === quotationId) return
		const c = quotation.configuration
		setConfig({
			seriesId: c?.series_id ?? undefined,
			pullCollectionId: c?.pull_collection_id ?? undefined,
			drawerBoxesMaterialId: c?.drawer_boxes_material_id ?? undefined,
			drawerSlideTypeId: c?.drawer_slide_type_id ?? undefined,
			hingesTypeId: c?.hinges_type_id ?? undefined,
			deliveryDates: c?.delivery_dates?.length
				? c.delivery_dates.map((d) => ({
						key: crypto.randomUUID(),
						id: d.id,
						date: d.date,
						name: d.project_name ?? undefined,
					}))
				: [createEmptyDeliveryDate()],
		})
		seededIdRef.current = quotationId ?? null
		setPendingDateAction(null)
	}, [isModalOpen, quotation, quotationId])

	const applyPendingDateAction = () => {
		if (!pendingDateAction) return
		const { type, index, date } = pendingDateAction
		setConfig((prev) => ({
			...prev,
			deliveryDates:
				type === "remove"
					? prev.deliveryDates.filter((_, i) => i !== index)
					: prev.deliveryDates.map((d, i) => (i === index ? { ...d, date } : d)),
		}))
		setPendingDateAction(null)
	}

	const handleSave = () => {
		if (!quotationId) return
		const body: TUpdateQuotationConfigurationRequest = {
			series_id: config.seriesId ?? null,
			pull_collection_id: config.pullCollectionId ?? null,
			drawer_slide_type_id: config.drawerSlideTypeId ?? null,
			drawer_boxes_material_id: config.drawerBoxesMaterialId ?? null,
			hinges_type_id: config.hingesTypeId ?? null,
			// Omitted entirely when no row carries a date — an empty list is what the backend rejects
			// with "Ensure this field has at least 1 elements" (#1341).
			...buildDeliveryDatesPayload(config.deliveryDates),
		}
		updateConfig(
			{ quotationId, body },
			{
				onSuccess: () => {
					success("Quotation configuration updated")
					setModalClose()
				},
				onError: (error) => formatError(error),
			},
		)
	}

	const confirmCopy = pendingDateAction ? DATE_CONFIRM_COPY[pendingDateAction.type] : null
	// The one delivery-date state the API cannot express — see the helper's comment.
	const clearedSavedDates = hasClearedSavedDeliveryDates(
		quotation?.configuration?.delivery_dates?.length ?? 0,
		config.deliveryDates,
	)

	return (
		<Modal isModalOpen={isModalOpen} setModalClose={setModalClose} width={600} title="Quotation configuration">
			<div className={style.body} data-testid="quotation-settings-modal">
				<QuotationConfiguration
					config={config}
					setConfig={setConfig}
					quotationId={quotationId}
					onRequestRemoveExistingDate={(index) => setPendingDateAction({ type: "remove", index })}
					onRequestChangeExistingDate={(index, date) => setPendingDateAction({ type: "change", index, date })}
				/>
				<div className={style.footer}>
					{clearedSavedDates ? <p className={style.footerError}>{CLEARED_SAVED_DATES_MESSAGE}</p> : null}
					<ButtonPrimary
						className={style.saveButton}
						onClick={handleSave}
						submittable={
							!!config.seriesId &&
							!isPending &&
							!hasIncompleteDeliveryDate(config.deliveryDates) &&
							!clearedSavedDates
						}
					>
						Save changes
					</ButtonPrimary>
				</div>
			</div>
			<ConfirmModal
				isOpen={!!pendingDateAction}
				onClose={() => setPendingDateAction(null)}
				onConfirm={applyPendingDateAction}
				title={confirmCopy?.title ?? ""}
				description={confirmCopy?.description ?? ""}
				confirmLabel={confirmCopy?.confirmLabel}
				getContainer={false}
			/>
		</Modal>
	)
}
